import React, {useCallback, useMemo, useEffect, useRef} from 'react';
import PropTypes from 'prop-types';
import SMCTableCell from './SMCTableCell';
import {SMCCheckbox} from '../../Simple/SMCCheckbox';
import { makeStyles } from '@mui/styles';
import clsx from 'clsx';
import get from 'lodash/get';
import SMCHighlighter from '../../Simple/SMCHighlighter';

function useShareForwardedRef(forwardedRef) {
    const innerRef = useRef(null);
  
    useEffect(() => {
        if (!forwardedRef) {
        return;
      }
      if (typeof forwardedRef === 'function') {
        forwardedRef(innerRef.current);
        return;
      } else {
        forwardedRef.current = innerRef.current;
      }
    });
  
    return innerRef;
}

function SMCTableRow({
    rowNumber,
    onCheckboxChange,
    onCheckboxShiftClick,
    content,
    textToHighlight,
    columns,
    columnsWidths,
    columnsState,
    selectedObjects,
    style,
    innerRef,
    hasCheckboxColumn=true,
    isSelectable,
    rowId,
    setHeight,
    onClickRow,
    allowRowWrap,
    showEditIcon,
    ...rest
}) {
    const classes = useStyles();
    const rowRootRef = useShareForwardedRef(innerRef);

    const objectSelected = selectedObjects && selectedObjects.includes(rowId);

    useEffect(() => {
        setHeight && rowRootRef.current && setHeight(rowNumber, rowRootRef.current.getBoundingClientRect().height)
    }, [setHeight, rowRootRef, rowNumber]);

    let rowSelectable = useMemo(() => {
        if (content.unselectable || isSelectable === false) {
            return false;
        }
        if (typeof isSelectable === 'function') {
            try {
                return isSelectable(content, rowNumber);
            } catch (e) {
                return false;
            }
        }
        return true;
    }, [content, isSelectable, rowNumber]);

    const checkboxOnToggle = useCallback(e => {
        if (e.nativeEvent.shiftKey) {
            onCheckboxShiftClick && onCheckboxShiftClick(rowId, e);    
        } else {
            onCheckboxChange && onCheckboxChange(rowId, e);
        }
    }, [rowId, onCheckboxShiftClick, onCheckboxChange]);

    const rowColumns = useMemo(() => {
        if (Array.isArray(columnsState)) {
            const shownColumns = columnsState.filter(colState => (colState.id && colState.show));
            return shownColumns.map((colState, index) => {
                const column = columns.find(col => col.id === colState.id);
                if (!column) {
                    return null;
                }
                const width = columnsWidths ? columnsWidths.find(col => col.id === column.id)?.width : column.width;
                const value = get(content, column.accessor);
                const Cell = column.Cell;

                return (
                    <React.Fragment key={column.id}>
                        <SMCTableCell width={width || column.width} align={column.align || "left"} allowRowWrap={allowRowWrap}>
                            {
                                (() => {
                                    if (Cell) {
                                        return <Cell value={value} object={content} index={rowNumber} textToHighlight={textToHighlight} showEditIcon={showEditIcon}/>
                                    }
                                    return value ? <SMCHighlighter highlight={textToHighlight} text={value} /> : null;
                                })()
                            }
                        </SMCTableCell>
                        {
                            // Cell separators, to align with the header separators
                            index !== (shownColumns.length - 1) && (
                                    <div className={classes.cellSeparator} key={index} />
                            )
                        }
                    </React.Fragment>
                );    
            })
        }
        else {
            return columns.map((column, index) => {
                const value = get(content, column.accessor);
                const Cell = column.Cell;
                const width = columnsWidths ? columnsWidths.find(col => col.id === column.id)?.width : column.width;

                return (
                    <React.Fragment key={column.id}>
                        <SMCTableCell width={width || column.width} align={column.align || "left"} allowRowWrap={allowRowWrap}>
                            {
                                (() => {
                                    if (Cell) {
                                        return <Cell value={value} object={content} index={rowNumber} />
                                    }
                                    return value ? <span>{value}</span> : null;
                                })()
                            }
                        </SMCTableCell>
                        {
                            // Cell separators, to align with the header separators
                            index !== (columns.length - 1) && (
                                    <div className={classes.cellSeparator} key={index} />
                            )
                        }
                    </React.Fragment>
                );
            })
        }
    }, [columns, columnsState, content, textToHighlight, rowNumber, columnsWidths, allowRowWrap, classes, showEditIcon]);

    return (
        <div
            onClick={onClickRow ? () => onClickRow(content) : undefined}
            ref={rowRootRef}
            className={clsx(rowNumber % 2 === 0 ? classes.evenRow : classes.oddRow, classes.root, onClickRow && classes.clickable)}
            style={style}
            {...rest}
        >
            {columnsState && <SMCTableCell className={classes.checkboxCell} width={12} />}
            {
                hasCheckboxColumn &&
                <SMCTableCell className={classes.checkboxCell}>
                    {
                        rowSelectable &&
                        <SMCCheckbox
                            checked={objectSelected}
                            onChange={checkboxOnToggle}
                            className={classes.checkbox}
                            inline
                        />
                    }
                </SMCTableCell>
            }
            { rowColumns }
        </div>
    );
}

const useStyles = makeStyles({
    root: {
        display: 'table',
        width: '100%',
        tableLayout: 'fixed',
        fontSize: '13px',
        // height: '32px',
        boxSizing: 'border-box',
        outline: 'none',
        '&:hover': {
            background: '#BCE3FE'
        },
        '& a': {
            textDecoration: 'none'
        }
    },
    oddRow: {
        background: '#F6F7F9',
		color: '#304461'
    },
    evenRow: {
        background: '#FFFFFF',
		color: '#333333'
    },
    checkboxCell: {
        width: '32px',
        minWidth: '32px',
        textAlign: 'center',
        borderLeft: 'none'
    },
    checkbox: {
        margin: 0
    },
    clickable: {
        cursor: 'pointer'
    },
    cellSeparator: {
        display: 'table-cell',
        width: '3px',
        boxSizing: 'border-box',
        height: '100%',
        padding: 0
    }
})

SMCTableRow.propTypes = {
    rowNumber: PropTypes.number.isRequired,
    content: PropTypes.object.isRequired,
    selectedObjects: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
    isDraggable: PropTypes.bool,
    onCheckboxChange: PropTypes.func,
    onCheckboxShiftClick: PropTypes.func,
    hasCheckboxColumn: PropTypes.bool,
    columns: PropTypes.arrayOf(PropTypes.object).isRequired,
    allowRowWrap: PropTypes.bool
}

export default React.memo(SMCTableRow);